<script setup lang="ts">
import { onBeforeUnmount, shallowRef, watch } from "vue"

import { getJson } from "@/lib/fetch"
import { ICON } from "@/lib/icons"
import { batchItemsPath } from "@/routes"

interface BatchItemResult {
  id: string
  name: string
  type: "item" | "container"
  location_path: string | null
}

const props = withDefaults(defineProps<{ disabled?: boolean }>(), { disabled: false })

const emit = defineEmits<{
  select: [itemId: string]
}>()

const query = shallowRef("")
const results = shallowRef<BatchItemResult[]>([])
const loading = shallowRef(false)
let timer: ReturnType<typeof setTimeout> | null = null
let request: AbortController | null = null

watch(query, (value) => {
  if (timer) clearTimeout(timer)
  request?.abort()
  const normalized = value.trim()
  if (!normalized) {
    results.value = []
    loading.value = false
    return
  }
  timer = setTimeout(() => search(normalized), 300)
})

onBeforeUnmount(() => {
  if (timer) clearTimeout(timer)
  request?.abort()
})

async function search(value: string) {
  const controller = new AbortController()
  request = controller
  loading.value = true
  try {
    const response = await getJson(
      `${batchItemsPath()}?q=${encodeURIComponent(value)}`,
      controller.signal,
    )
    if (!response.ok || controller.signal.aborted) return
    const body = (await response.json()) as { data: BatchItemResult[] }
    if (controller.signal.aborted) return
    results.value = body.data
  } catch (error) {
    if (!(error instanceof DOMException && error.name === "AbortError")) throw error
  } finally {
    if (request === controller) {
      request = null
      loading.value = false
    }
  }
}
</script>

<template>
  <div class="space-y-2">
    <label class="text-highlighted block text-sm font-medium" for="batch-item-search">
      Attach to existing item
    </label>
    <div class="relative">
      <UIcon :name="ICON.search" class="text-muted absolute top-3.5 left-3 size-4" />
      <input
        id="batch-item-search"
        v-model="query"
        type="search"
        :disabled="disabled"
        placeholder="Search inventory"
        autocomplete="off"
        class="border-default bg-default min-h-11 w-full rounded-md border pr-3 pl-9 text-sm disabled:opacity-50"
      />
    </div>
    <p v-if="loading" class="text-muted text-sm" role="status">Searching…</p>
    <div
      v-else-if="results.length"
      class="border-default divide-default divide-y rounded-md border"
    >
      <button
        v-for="item in results"
        :key="item.id"
        type="button"
        :data-testid="`attach-item-${item.id}`"
        :disabled="disabled"
        class="hover:bg-elevated flex min-h-12 w-full items-center gap-3 px-3 text-left disabled:opacity-50"
        @click="emit('select', item.id)"
      >
        <UIcon
          :name="item.type === 'container' ? ICON.container : ICON.item"
          class="text-muted size-4"
        />
        <span class="min-w-0 flex-1">
          <span class="text-highlighted block truncate text-sm font-medium">{{ item.name }}</span>
          <span v-if="item.location_path" class="text-muted block truncate text-xs">
            {{ item.location_path }}
          </span>
        </span>
      </button>
    </div>
    <p v-else-if="query.trim()" class="text-muted text-sm">No matching inventory items.</p>
  </div>
</template>
